import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import { permissionService } from '../../services/admin/permissions.service';
import { roleService } from '../../services/admin/roles.service';
import type {
  AllPermissionsResponse,
  Permission,
  PermissionGroup,
  RolePermissionsResponse,
  UpdatePermissionsRequest,
} from '../../services/admin/role.types';

/**
 * Query keys for permission-related queries
 */
export const permissionKeys = {
  all: ['permissions'] as const,
  allPermissions: () => [...permissionKeys.all, 'allPermissions'] as const,
  groups: () => [...permissionKeys.all, 'groups'] as const,
  byProvider: (provider: string) => [...permissionKeys.all, 'provider', provider] as const,
  search: (query: string) => [...permissionKeys.all, 'search', query] as const,
  rolePermissions: (roleName: string) => [...permissionKeys.all, 'role', roleName] as const,
  hierarchy: () => [...permissionKeys.all, 'hierarchy'] as const,
  stats: () => [...permissionKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch all available permissions
 * Results are cached for 5 minutes
 *
 * @param forceRefresh - Force refresh from API
 * @returns React Query result with all permissions
 *
 * @example
 * ```tsx
 * function PermissionsList() {
 *   const { data, isLoading } = useAllPermissions();
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <h2>Total Permissions: {data.permissions.length}</h2>
 *       {data.permissions.map(perm => (
 *         <PermissionCard key={perm.name} permission={perm} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAllPermissions(
  forceRefresh: boolean = false
): UseQueryResult<AllPermissionsResponse, Error> {
  return useQuery({
    queryKey: permissionKeys.allPermissions(),
    queryFn: () => permissionService.getAllPermissions(forceRefresh),
    staleTime: 1000 * 60 * 5, // 5 minutes - permissions rarely change
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch permissions grouped by provider
 *
 * @param forceRefresh - Force refresh from API
 * @returns React Query result with permission groups
 *
 * @example
 * ```tsx
 * function PermissionsGrouped() {
 *   const { data: groups, isLoading } = usePermissionGroups();
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {groups.map(group => (
 *         <PermissionGroup key={group.name} group={group} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissionGroups(
  forceRefresh: boolean = false
): UseQueryResult<PermissionGroup[], Error> {
  return useQuery({
    queryKey: permissionKeys.groups(),
    queryFn: () => permissionService.getPermissionGroups(forceRefresh),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Hook to fetch permissions for a specific provider
 *
 * @param providerName - Provider name (e.g., 'Identity', 'User')
 * @param forceRefresh - Force refresh from API
 * @returns React Query result with provider permissions
 *
 * @example
 * ```tsx
 * function IdentityPermissions() {
 *   const { data: permissions } = useProviderPermissions('Identity');
 *
 *   return (
 *     <div>
 *       <h2>Identity Permissions ({permissions?.length})</h2>
 *       {permissions?.map(perm => <PermissionItem key={perm.name} permission={perm} />)}
 *     </div>
 *   );
 * }
 * ```
 */
export function useProviderPermissions(
  providerName: string,
  forceRefresh: boolean = false,
  options?: { enabled?: boolean }
): UseQueryResult<Permission[], Error> {
  return useQuery({
    queryKey: permissionKeys.byProvider(providerName),
    queryFn: () => permissionService.getProviderPermissions(providerName, forceRefresh),
    enabled: options?.enabled !== false && !!providerName,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to search permissions
 *
 * @param query - Search query
 * @param forceRefresh - Force refresh from API
 * @returns React Query result with filtered permissions
 *
 * @example
 * ```tsx
 * function PermissionSearch() {
 *   const [searchQuery, setSearchQuery] = useState('');
 *   const { data: results, isLoading } = useSearchPermissions(searchQuery);
 *
 *   return (
 *     <div>
 *       <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
 *       {isLoading ? <Spinner /> : <PermissionList permissions={results} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSearchPermissions(
  query: string,
  forceRefresh: boolean = false,
  options?: { enabled?: boolean }
): UseQueryResult<Permission[], Error> {
  return useQuery({
    queryKey: permissionKeys.search(query),
    queryFn: () => permissionService.searchPermissions(query, forceRefresh),
    enabled: (options?.enabled !== false && query.trim().length > 0) || false,
    staleTime: 1000 * 60 * 2, // 2 minutes for search results
  });
}

/**
 * Hook to fetch permissions assigned to a role
 *
 * @param roleName - Role name
 * @returns React Query result with role permissions
 *
 * @example
 * ```tsx
 * function RolePermissions({ roleName }: { roleName: string }) {
 *   const { data, isLoading } = useRolePermissions(roleName);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <h2>{data.entityDisplayName} Permissions</h2>
 *       {data.groups.map(group => (
 *         <PermissionGroup key={group.name} group={group} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useRolePermissions(
  roleName: string,
  options?: { enabled?: boolean }
): UseQueryResult<RolePermissionsResponse, Error> {
  return useQuery({
    queryKey: permissionKeys.rolePermissions(roleName),
    queryFn: () => roleService.getRolePermissions(roleName),
    // Only enable query if roleName is provided and not empty
    enabled: options?.enabled !== false && !!roleName && roleName.trim().length > 0,
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

/**
 * Hook to get permission hierarchy (parent-child relationships)
 *
 * @param forceRefresh - Force refresh from API
 * @returns React Query result with permission hierarchy
 *
 * @example
 * ```tsx
 * function PermissionTree() {
 *   const { data: hierarchy } = usePermissionHierarchy();
 *
 *   return <TreeView data={hierarchy} />;
 * }
 * ```
 */
export function usePermissionHierarchy(
  forceRefresh: boolean = false
): UseQueryResult<Map<string, Permission[]>, Error> {
  return useQuery({
    queryKey: permissionKeys.hierarchy(),
    queryFn: () => permissionService.getPermissionHierarchy(forceRefresh),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to get permission statistics
 *
 * @param forceRefresh - Force refresh from API
 * @returns React Query result with permission statistics
 *
 * @example
 * ```tsx
 * function PermissionStats() {
 *   const { data: stats } = usePermissionStatistics();
 *
 *   return (
 *     <div>
 *       <p>Total Permissions: {stats?.total}</p>
 *       <p>Providers: {stats?.providerCount}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissionStatistics(
  forceRefresh: boolean = false
): UseQueryResult<{
  total: number;
  providerCount: number;
  providers: { name: string; count: number }[];
}, Error> {
  return useQuery({
    queryKey: permissionKeys.stats(),
    queryFn: () => permissionService.getPermissionStatistics(forceRefresh),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to update role permissions
 *
 * @returns Mutation result with update function
 *
 * @example
 * ```tsx
 * function RolePermissionsForm({ roleName }: { roleName: string }) {
 *   const updatePermissions = useUpdateRolePermissions();
 *   const [selectedPerms, setSelectedPerms] = useState<string[]>([]);
 *
 *   const handleSave = async () => {
 *     try {
 *       await updatePermissions.mutateAsync({
 *         roleName,
 *         permissions: {
 *           permissions: selectedPerms.map(name => ({ name, isGranted: true }))
 *         }
 *       });
 *       toast.success('Permissions updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update permissions');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <PermissionSelector selected={selectedPerms} onChange={setSelectedPerms} />
 *       <Button onClick={handleSave} disabled={updatePermissions.isPending}>
 *         Save Permissions
 *       </Button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useUpdateRolePermissions(): UseMutationResult<
  void,
  Error,
  { roleName: string; permissions: UpdatePermissionsRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleName, permissions }) =>
      roleService.updateRolePermissions(roleName, permissions),
    onSuccess: (_, variables) => {
      // Invalidate role permissions to refetch
      queryClient.invalidateQueries({
        queryKey: permissionKeys.rolePermissions(variables.roleName),
      });

      // Optionally invalidate role details if they cache permission counts
      queryClient.invalidateQueries({ queryKey: ['roles', 'detail'] });
    },
  });
}

/**
 * Hook to clear permissions cache
 * Useful when permissions are added/removed system-wide
 *
 * @example
 * ```tsx
 * function ClearCacheButton() {
 *   const clearCache = useClearPermissionsCache();
 *
 *   return (
 *     <Button onClick={clearCache}>
 *       Refresh Permissions
 *     </Button>
 *   );
 * }
 * ```
 */
export function useClearPermissionsCache() {
  const queryClient = useQueryClient();

  return () => {
    permissionService.clearCache();
    queryClient.invalidateQueries({ queryKey: permissionKeys.all });
  };
}
